import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, ArrowLeft, CheckCircle2, AlertTriangle, ShieldCheck, Thermometer, Wind, Wrench, Sparkles, Clock, ArrowRight, Award, Zap, Gamepad2, Send } from 'lucide-react';
import Footer from '../components/Footer';
import HeaderSocials from '../components/HeaderSocials';
import HostedVideoSection from '../components/HostedVideoSection';
import InstagramPostLinks from '../components/InstagramPostLinks';
import LandingFaqSchema from '../components/LandingFaqSchema';
import useLandingContent from '../hooks/useLandingContent';

const CONSOLES = {
  ps5: {
    label: 'PlayStation 5',
    short: 'PS5',
    tag: 'Sony',
    hero: '/images/generated/console-ps5-hero.png',
    fallback: '/images/generated/console-ps5.png',
    price: 1800,
    duration: '3–4 год',
    warranty: '30 днів',
    accent: 'from-cyan-400 to-blue-500',
    glow: 'shadow-[0_0_80px_rgba(59,130,246,0.45)]',
    bgGlow: 'rgba(59,130,246,0.25)',
    features: [
      'Розбирання зі зняттям блоку живлення та материнської плати',
      'Демонтаж системи охолодження (liquid metal обережно)',
      'Ультразвукова чистка радіатора та кулера',
      'Заміна liquid metal термоінтерфейсу (для PS5) на еквівалент або якісну термопасту',
      'Заміна термопрокладок VRAM/VRM',
      'Тестування температур під навантаженням 2 години'
    ]
  },
  ps4: {
    label: 'PlayStation 4 / Pro',
    short: 'PS4',
    tag: 'Sony',
    hero: '/images/generated/console-ps4-hero.png',
    fallback: '/images/generated/console-ps4.png',
    price: 700,
    duration: '1.5–2 год',
    warranty: '30 днів',
    accent: 'from-orange-400 to-orange-600',
    glow: 'shadow-[0_0_80px_rgba(255,140,0,0.45)]',
    bgGlow: 'rgba(255,140,0,0.25)',
    features: [
      'Повне розбирання корпусу',
      'Чистка кулера, радіатора, БП від пилу',
      'Заміна термопасти преміум-класу (Arctic MX-6 або аналог)',
      'Заміна термопрокладок за необхідності',
      'Змазка підшипника кулера',
      'Тестування у навантаженні + перевірка BluRay-приводу'
    ]
  },
  xbox: {
    label: 'Xbox Series X / One',
    short: 'Xbox',
    tag: 'Microsoft',
    hero: '/images/generated/console-xbox-hero.png',
    fallback: '/images/generated/console-xbox.png',
    price: 900,
    duration: '2–3 год',
    warranty: '30 днів',
    accent: 'from-emerald-400 to-green-500',
    glow: 'shadow-[0_0_80px_rgba(34,197,94,0.45)]',
    bgGlow: 'rgba(34,197,94,0.25)',
    features: [
      'Повне розбирання корпусу з демонтажем плати',
      'Чистка радіатора, вентилятора та БП від пилу',
      'Заміна термопасти преміум-класу',
      'Заміна термопрокладок VRAM/VRM',
      'Змазка підшипника кулера',
      'Тестування у навантаженні + перевірка дисководу'
    ]
  }
};

const STEPS = [
  { title: 'Безкоштовна діагностика', desc: 'Заміряємо температури, слухаємо кулер, оглядаємо консоль. Даємо чесний висновок — чи потрібна чистка.', img: '/images/generated/console-step-1.png', icon: ShieldCheck },
  { title: 'Розбирання', desc: 'Демонтуємо корпус, блок живлення, материнську плату. Все відбувається в антистатичних умовах на професійному робочому місці.', img: '/images/generated/console-step-1.png', icon: Wrench },
  { title: 'Показуємо що всередині', desc: 'Фото «до» надсилаємо у Telegram. Ви побачите весь пил і бруд, який заважає охолодженню вашої консолі.', img: '/images/generated/console-step-2.png', icon: AlertTriangle },
  { title: 'Глибока чистка', desc: 'Стисненим повітрям, м\'якими щітками і спеціальними розчинами видаляємо весь пил з радіатора, кулера, плати.', img: '/images/generated/console-step-3.png', icon: Wind },
  { title: 'Заміна термопасти', desc: 'Наносимо преміум-термопасту (Arctic MX-6, Thermal Grizzly). Для PS5 — за необхідності liquid metal-еквівалент.', img: '/images/generated/console-step-4.png', icon: Thermometer },
  { title: 'Тест 2 години', desc: 'Прогріваємо у грі під максимальним навантаженням. Показуємо температури «до і після» — різниця в середньому −20°C.', img: '/images/generated/console-step-5.png', icon: Zap }
];

const SIGNS = [
  { icon: '🔊', text: 'Кулер шумить як реактивний двигун' },
  { icon: '🥵', text: 'Консоль вимикається сама у важких іграх' },
  { icon: '📉', text: 'Падає FPS, тормозить графіка' },
  { icon: '🌡️', text: 'Корпус став гарячим до дотику' },
  { icon: '🎮', text: 'Ігри вилітають, з\'явилися артефакти' },
  { icon: '⏰', text: 'Не чистили більше 12 місяців' }
];

const FAQS = [
  { q: 'Чи є ризик, що після чистки консоль зламається?', a: 'Наш майстер має 8+ років досвіду. За весь час — жодного втраченого пристрою. Даємо офіційну гарантію 30 днів на чистку.' },
  { q: 'Скільки триває процедура?', a: 'PS4 — 1.5–2 години, PS5 — 3–4 години, Xbox Series X — 2–3 години. Можна почекати або залишити з ранку і забрати ввечері.' },
  { q: 'Ви замінюєте liquid metal на PS5?', a: 'Оригінальний liquid metal вкрай складно нанести без спеціалізованого обладнання. Ми пропонуємо або якісний liquid metal-еквівалент, або преміум-термопасту з термопрокладками — температури будуть на 3–5°C нижче стокових.' },
  { q: 'Що робити з ліцензійними іграми та збереженнями?', a: 'Ми чистимо лише «залізо» — до диску, акаунту та збережень ніхто не торкається. Все залишиться на місці.' },
  { q: 'Виїжджаєте додому?', a: 'Ні, чистка виконується виключно у нашому сервіс-центрі — потрібне професійне антистатичне обладнання та освітлення.' }
];

const ConsoleCleaningPage = () => {
  const [active, setActive] = useState('ps5');
  const [imgError, setImgError] = useState({});
  const landing = useLandingContent('ps5');

  useEffect(() => {
    Object.values(CONSOLES).forEach((con) => {
      if (con.hero) { const img = new Image(); img.src = con.hero; }
      if (con.fallback) { const img = new Image(); img.src = con.fallback; }
    });
  }, []);
  const c = CONSOLES[active];

  const handleImgError = (key) => setImgError((s) => ({ ...s, [key]: true }));
  const heroSrc = imgError[active] ? c.fallback : c.hero;

  return (
    <div className="min-h-screen console-page" data-testid="console-cleaning-page">
      <Helmet>
        <title>Чистка ігрових консолей в Одесі — PS5, PS4, Xbox | CIMM Computers</title>
        <meta name="description" content="Професійна чистка ігрових консолей PlayStation 5, PlayStation 4, Xbox Series X в Одесі. Заміна термопасти преміум-класу, зниження температур до −20°C. Термін від 1.5 год. Гарантія 30 днів. ☎ +38 097 417 8788" />
        <meta name="keywords" content="чистка консолей Одеса, чистка приставок Одеса, чистка PS5 Одеса, чистка PS4 Одеса, чистка Xbox Одеса, заміна термопасти PlayStation, ремонт консолей Одеса" />
        <link rel="canonical" href="https://cimm.od.ua/chistka-konsoley" />
        <meta property="og:title" content="Чистка ігрових консолей в Одесі — CIMM Computers" />
        <meta property="og:description" content="Професійна чистка PS5 / PS4 / Xbox з гарантією. Зниження температур до −20°C." />
        <meta property="og:image" content="https://cimm.od.ua/images/generated/console-hero.png" />
        <meta property="og:type" content="service" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Чистка ігрових консолей',
          provider: {
            '@type': 'LocalBusiness',
            name: 'CIMM Computers',
            telephone: '+380974178788',
            address: { '@type': 'PostalAddress', addressLocality: 'Одеса', addressCountry: 'UA' }
          },
          areaServed: { '@type': 'City', name: 'Одеса' },
          offers: [
            { '@type': 'Offer', name: 'Чистка PS5', price: '1800', priceCurrency: 'UAH' },
            { '@type': 'Offer', name: 'Чистка PS4 / PS4 Pro', price: '700', priceCurrency: 'UAH' },
            { '@type': 'Offer', name: 'Чистка Xbox Series X', price: '900', priceCurrency: 'UAH' }
          ]
        })}</script>
      </Helmet>
      <LandingFaqSchema category="consoles" />

      {/* Header */}
      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-home-link">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-orange-400/80 transition-all">Головна</Link>
            <Link to="/phones" className="text-gray-300 hover:text-orange-400/80 transition-all">Телефони</Link>
            <Link to="/laptops" className="text-gray-300 hover:text-orange-400/80 transition-all">Ноутбуки</Link>
            <Link to="/chistka-noutbukiv" className="text-gray-300 hover:text-orange-400/80 transition-all">Чистка ноутбуків</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-orange-400/80 transition-all">Контакти</Link>
          </nav>
          <div className="flex items-center gap-2">
            <HeaderSocials />
            <a href="tel:+380974178788" className="neon-text font-semibold" data-testid="header-phone-link">+38 097 417 8788</a>
          </div>
        </div>
      </header>

      {/* Hero — console image visible as prominent right-side background */}
      <section className="relative min-h-[92vh] md:min-h-[88vh] flex items-center overflow-hidden pt-28 pb-16 md:pt-24 md:pb-20 console-hero">
        {/* Ambient colored glow (behind everything) */}
        <div className="absolute inset-0 pointer-events-none console-hero-bg" aria-hidden="true">
          <div
            className="absolute inset-0 transition-all duration-700"
            style={{ background: `radial-gradient(ellipse 70% 90% at 75% 50%, ${c.bgGlow}, transparent 65%)` }}
          />
        </div>

        {/* Right side: console image (visible, positioned) */}
        <div className="absolute inset-y-0 right-0 w-full md:w-[62%] lg:w-[58%] pointer-events-none overflow-hidden">
          <img
            src={heroSrc}
            alt={c.label}
            onError={() => handleImgError(active)}
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-contain object-right transition-opacity duration-500"
            data-testid={`hero-console-img-${active}`}
          />
          {/* Vertical fade on left edge for text readability */}
          <div className="absolute inset-y-0 left-0 w-1/2 console-hero-fade" />
        </div>

        <div className="container mx-auto max-w-7xl px-4 relative z-10">
          {/* Top row: switcher centered above content */}
          <div className="flex flex-col items-center mb-10">
            <Link to="/" className="self-start inline-flex items-center gap-2 hero-back-link mb-6 transition-colors text-sm" data-testid="back-home-link">
              <ArrowLeft className="w-4 h-4" /> На головну
            </Link>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/30 mb-5 backdrop-blur-sm">
              <Gamepad2 className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-xs uppercase tracking-widest text-orange-400">Чистка ігрових консолей · Одеса</span>
            </div>

            <div className="inline-flex p-1.5 rounded-full console-switcher-bg backdrop-blur-xl border shadow-2xl" data-testid="console-switcher">
              {Object.entries(CONSOLES).map(([key, con]) => (
                <button
                  key={key}
                  onClick={() => setActive(key)}
                  data-testid={`switcher-${key}`}
                  className={`px-5 md:px-7 py-2.5 rounded-full text-sm md:text-base font-semibold transition-all duration-500 ${
                    active === key
                      ? `bg-gradient-to-r ${con.accent} text-white shadow-lg`
                      : 'hero-switcher-idle hover:opacity-100'
                  }`}
                >
                  {con.short}
                </button>
              ))}
            </div>
          </div>

          {/* Content — left column with text over the background image */}
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.02] mb-5 hero-h1">
              Чистка{' '}
              <span className={`bg-gradient-to-r ${c.accent} bg-clip-text text-transparent whitespace-nowrap`}>
                {c.short}
              </span>
              <br />в Одесі
            </h1>
            <p className="text-lg md:text-xl mb-6 max-w-xl hero-lead">
              Ваша {c.short} перестане звучати як пилосос. Знижуємо температури в середньому на <span className="font-bold hero-lead-strong">−20°C</span>. Термін від <span className="font-bold hero-lead-strong">{c.duration}</span>. Гарантія <span className="font-bold hero-lead-strong">{c.warranty}</span>.
            </p>

            <div className="flex flex-wrap items-baseline gap-3 mb-8">
              <div className="hero-price-from text-sm">від</div>
              <div className={`text-6xl md:text-7xl font-black bg-gradient-to-r ${c.accent} bg-clip-text text-transparent`}>
                {c.price}
              </div>
              <div className="hero-price-unit text-2xl font-bold">грн</div>
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="tel:+380974178788"
                data-testid="hero-cta-call"
                className={`inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r ${c.accent} text-white font-bold rounded-full transition-all duration-300 hover:scale-105 ${c.glow}`}
              >
                <Phone className="w-5 h-5" /> Записатись зараз
              </a>
              <a
                href="#steps"
                data-testid="hero-cta-details"
                className="inline-flex items-center gap-2 px-8 py-4 hero-cta-secondary font-semibold rounded-full border transition-all"
              >
                Як ми це робимо <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="flex flex-wrap gap-6 mt-8 text-sm hero-meta">
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-orange-400" /> {c.duration}</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-orange-400" /> Гарантія {c.warranty}</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-orange-400" /> Діагностика 0 грн</div>
            </div>
          </div>

          {/* Floating right-side badge */}
          <div className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 z-10 hidden lg:block">
            <div className="glass-card px-5 py-4 backdrop-blur-2xl border text-center rotate-3 hero-effect-badge">
              <div className="text-[10px] uppercase tracking-widest hero-effect-label">Ефект</div>
              <div className={`text-4xl font-black bg-gradient-to-r ${c.accent} bg-clip-text text-transparent`}>−20°C</div>
              <div className="text-xs hero-effect-sub mt-1">після чистки</div>
            </div>
          </div>
        </div>
      </section>

      <HostedVideoSection url={active === 'ps5' ? landing?.uploaded_video : null} />

      {/* Features included */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="glass-card p-6 md:p-10 backdrop-blur-2xl bg-white/[0.03] border border-white/10">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-1">
                <div className="text-xs uppercase tracking-widest text-orange-400 mb-2">Що входить</div>
                <h3 className="text-2xl md:text-3xl font-black text-white leading-tight">Повний профілактичний комплекс для {c.short}</h3>
                <p className="text-gray-400 text-sm mt-3">Все включено у ціну. Ніяких прихованих доплат.</p>
              </div>
              <ul className="md:col-span-2 grid sm:grid-cols-2 gap-3">
                {c.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 bg-gradient-to-r ${c.accent} bg-clip-text text-transparent`} style={{ color: 'currentColor' }} />
                    <span className="text-sm md:text-base">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Signs */}
      <section className="py-14 md:py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,140,0,0.05),transparent_50%)] -z-10" />
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-orange-400 mb-2">Симптоми</div>
            <h2 className="text-3xl md:text-5xl font-black text-white">
              Коли пора йти на <span className="neon-text">чистку</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SIGNS.map((s, i) => (
              <div key={i} className="glass-card p-5 hover:border-orange-500/50 transition-all" data-testid={`sign-${i}`}>
                <div className="text-3xl mb-2">{s.icon}</div>
                <p className="text-gray-300 text-sm md:text-base">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process steps */}
      <section id="steps" className="py-14 md:py-24 px-4 relative">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-widest text-orange-400 mb-2">Процес</div>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4">
              6 етапів <span className="neon-text">ідеальної чистки</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Прозоро від першого дзвінка до тестування. Ми фотографуємо кожен етап і надсилаємо вам у Telegram.</p>
          </div>

          <div className="space-y-8 md:space-y-16">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const reverse = i % 2 === 1;
              return (
                <div
                  key={i}
                  className={`grid md:grid-cols-2 gap-6 md:gap-10 items-center ${reverse ? 'md:[direction:rtl]' : ''}`}
                  data-testid={`step-${i}`}
                >
                  <div className="md:[direction:ltr]">
                    <div className="rounded-2xl overflow-hidden border border-white/10 relative aspect-[4/3] bg-black/40">
                      <img src={s.img} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute top-3 left-3 w-10 h-10 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-orange-400 font-black text-lg border border-orange-500/40">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                    </div>
                  </div>
                  <div className="md:[direction:ltr]">
                    <div className={`inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-gradient-to-r ${c.accent} bg-opacity-10`}>
                      <Icon className="w-4 h-4 text-white" />
                      <span className="text-xs uppercase tracking-wider text-white font-semibold">Крок {i + 1}</span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-black text-white mb-3">{s.title}</h3>
                    <p className="text-gray-300 text-base md:text-lg leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Temperature comparison */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="glass-card p-8 md:p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(255,140,0,0.08),transparent_50%)]" />
            <div className="relative z-10 grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-xs uppercase tracking-widest text-red-400 mb-2">До чистки</div>
                <div className="text-6xl md:text-7xl font-black text-white mb-1">85°C</div>
                <div className="text-gray-400 text-sm">APU під навантаженням</div>
              </div>
              <div className="flex items-center justify-center">
                <div className={`text-5xl bg-gradient-to-r ${c.accent} bg-clip-text text-transparent`}>→</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-green-400 mb-2">Після чистки</div>
                <div className={`text-6xl md:text-7xl font-black bg-gradient-to-r ${c.accent} bg-clip-text text-transparent mb-1`}>65°C</div>
                <div className="text-gray-400 text-sm">Комфортні для життя чипа</div>
              </div>
            </div>
            <p className="text-center text-gray-400 text-sm mt-6 max-w-2xl mx-auto">
              Середні показники наших {c.short} після чистки з заміною термопасти.
            </p>
          </div>
        </div>
      </section>

      {/* Price + trust */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl grid md:grid-cols-2 gap-8 items-stretch">
          <div className="glass-card p-8 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-400 mb-3">Прайс</div>
              <h3 className="text-3xl font-black text-white mb-6">Ціна на чистку {c.short}</h3>
              <div className="flex items-baseline gap-3 mb-6">
                <div className="text-gray-400">від</div>
                <div className={`text-7xl font-black bg-gradient-to-r ${c.accent} bg-clip-text text-transparent`}>{c.price}</div>
                <div className="text-gray-400 text-2xl">грн</div>
              </div>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" /> Термопаста преміум-класу входить у ціну</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" /> Термопрокладки — за необхідності (+150–300 грн)</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" /> Діагностика та тестування — 0 грн</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" /> Гарантія на послугу — {c.warranty}</li>
              </ul>
            </div>
            <a
              href="tel:+380974178788"
              data-testid="price-cta-call"
              className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r ${c.accent} text-white font-bold rounded-full text-lg ${c.glow}`}
            >
              <Phone className="w-5 h-5" /> Записатись за 30 сек
            </a>
          </div>
          <div className="glass-card p-8 space-y-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-400 mb-3">Чому нас</div>
              <h3 className="text-3xl font-black text-white">Обирають в Одесі</h3>
            </div>
            <div className="space-y-4">
              {[
                { n: '8+', l: 'років ремонтуємо консолі' },
                { n: '3200+', l: 'приставок пройшло через наші руки' },
                { n: '−20°C', l: 'середнє зниження температури' },
                { n: '30 днів', l: 'офіційної гарантії' }
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className={`text-3xl md:text-4xl font-black bg-gradient-to-r ${c.accent} bg-clip-text text-transparent min-w-[100px]`}>{s.n}</div>
                  <div className="text-gray-300">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-orange-400 mb-2">FAQ</div>
            <h2 className="text-3xl md:text-5xl font-black text-white">Часті питання</h2>
          </div>
          <div className="space-y-3" data-testid="faq-section">
            {FAQS.map((f, i) => (
              <details key={i} className="glass-card p-6 group cursor-pointer" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between text-white font-semibold list-none">
                  <span className="pr-4">{f.q}</span>
                  <span className="text-orange-400 text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-400 mt-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 md:py-24 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="glass-card p-8 md:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,140,0,0.12),transparent_60%)]" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4">
                Ваша {c.short} заслуговує <span className="neon-text">тиші</span>
              </h2>
              <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto">
                Один дзвінок — і вже через {c.duration} ви заберете консоль, яка працює як нова.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a href="tel:+380974178788" data-testid="final-cta-call" className={`inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r ${c.accent} text-white font-bold rounded-full text-lg ${c.glow}`}>
                  <Phone className="w-5 h-5" /> +38 097 417 8788
                </a>
                <a href="https://t.me/cimmodua" target="_blank" rel="noopener noreferrer" data-testid="final-cta-telegram" className="inline-flex items-center gap-2 px-10 py-4 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-full border border-white/10 transition-all">
                  <Send className="w-5 h-5" /> Написати в Telegram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <InstagramPostLinks
        urls={landing?.instagram_posts?.length ? landing.instagram_posts : [landing?.instagram_video]}
      />

      <Footer />
    </div>
  );
};

export default ConsoleCleaningPage;
